import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { NativePurchases } from '@capgo/native-purchases';
import { useAuth } from "../../App"; 
import DashboardHeader from "../../components/app_components/DashboardHeader";
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import { 
  Home, BellDot, Bell, ShoppingBag, Settings, 
  Users, ShoppingCart, User, Mail, Globe, 
  Shield, ChevronRight, Loader2, Share2, CheckCircle, 
  XCircle, Trash2, AlertTriangle, Crown
} from 'lucide-react';
import { useTranslation } from '../../contexts/LanguageContext';
import FlagLanguageSelector from '../../components/LanguageSelector/FlagLanguageSelector';

const MobileAccountSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, validateToken, httpClient } = useAuth();
  
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [resetStatus, setResetStatus] = useState({
    loading: false,
    success: false,
    error: null
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteStatus, setDeleteStatus] = useState({
    loading: false,
    success: false,
    error: null
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [cancelStatus, setCancelStatus] = useState({ success: false, error: null });

  const { t } = useTranslation();

  // Fetch unread notifications
  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Fetch user data from API
  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!httpClient) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      
      const response = await httpClient.request('/api/user/account');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setUserData(data);
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      fetchUserData();
      
      const interval = setInterval(() => {
        fetchUnreadNotifications();
      }, 30000);
      
      return () => clearInterval(interval);
    } else {
      navigate("/demo");
    }
  }, [user, navigate]);

  const navItems = [
    { icon: Home, label: t('bottomNav.home'), path: "/mobile-dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications'),
      path: "/mobile-notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace'), path: "/mobile-marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs'), path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank'), path: "/mobile-connectBank" },
    { icon: Users, label: t('bottomNav.partners'), path: "/mobile-partners" },
    { icon: Settings, label: t('bottomNav.settings'), path: "/mobile-account-settings" },
  ];

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!userData?.email) return;
    
    setResetStatus({ loading: true, success: false, error: null });
    
    try {
      if (!httpClient) {
        setResetStatus({ loading: false, success: false, error: 'Not authenticated' });
        return;
      }
      
      const response = await httpClient.request('/api/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email: userData.email })
      });

      if (!response.ok) {
        throw new Error(response.status === 404 
          ? 'User not found' 
          : 'Failed to send reset email');
      }

      const result = await response.json();
      if (result.message) {
        setResetStatus({ loading: false, success: true, error: null });
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setResetStatus({ loading: false, success: false, error: err.message });
    }
  };

  // Handle manage subscription
  const handleManageSubscription = async () => {
    const platform = userData?.subscription?.paymentPlatform;
    
    if (platform === 'apple' || Capacitor.getPlatform() === 'ios') {
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        try {
          await NativePurchases.manageSubscriptions();
        } catch (err) {
          window.location.href = 'itms-apps://apps.apple.com/account/subscriptions';
        }
      } else {
        window.open('https://apps.apple.com/account/subscriptions', '_blank');
      }
    } else {
      setShowCancelModal(true);
    }
  };

  // Handle Stripe cancellation
  const handleStripeCancel = async () => {
    setCancellingSubscription(true);
    setCancelStatus({ success: false, error: null });
    
    try {
      const response = await httpClient.request('/api/subscription/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('accountSettings.subscription.cancelFailed'));
      }
      
      setCancelStatus({ success: true, error: null });
      setShowCancelModal(false);
      fetchUserData();
    } catch (err) {
      console.error('Cancel subscription error:', err);
      setCancelStatus({ 
        success: false, 
        error: err.message || t('accountSettings.subscription.cancelFailed')
      });
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== 'goodbye easysubs') {
      setDeleteStatus({
        loading: false,
        success: false,
        error: t('accountSettings.dangerZone.confirmationError')
      });
      return;
    }

    setDeleteStatus({ loading: true, success: false, error: null });
    
    try {
      if (!httpClient) {
        setDeleteStatus({ 
          loading: false, 
          success: false, 
          error: t('common.notAuthenticated')
        });
        return;
      }

      const response = await httpClient.request('/api/user/account', {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(t('accountSettings.dangerZone.deleteFailed'));
      }

      setDeleteStatus({ loading: false, success: true, error: null });
      
      setTimeout(() => {
        logout();
        navigate('/demo');
      }, 2000);
    } catch (err) {
      console.error('Account deletion error:', err);
      setDeleteStatus({ 
        loading: false, 
        success: false, 
        error: err.message || t('accountSettings.dangerZone.deleteError')
      });
    }
  };

  // Render security section
  const renderSecuritySection = () => {
    if (resetStatus.success) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">{t('accountSettings.security.emailSent')}</p>
              <p className="text-xs text-green-600 mt-1">
                {t('accountSettings.security.emailSentDescription').replace('{email}', userData?.email)}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (resetStatus.loading) {
      return (
        <button 
          disabled
          className="w-full flex justify-between items-center p-3 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed"
        >
          <div>
            <p className="font-medium text-left">{t('accountSettings.security.sendingEmail')}</p>
            <p className="text-xs text-gray-500 text-left">{t('accountSettings.security.pleaseWait')}</p>
          </div>
          <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
        </button>
      );
    }

    if (resetStatus.error) {
      return (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start">
              <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">
                  {t('accountSettings.security.errorTitle')}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {resetStatus.error || t('accountSettings.security.tryAgainLater')}
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handlePasswordReset}
            className="w-full flex justify-between items-center p-3 border border-gray-200 rounded-lg"
          >
            <div>
              <p className="font-medium text-left">{t('accountSettings.security.tryAgain')}</p>
              <p className="text-xs text-gray-500 text-left">{t('accountSettings.security.resendEmail')}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={handlePasswordReset}
        className="w-full flex justify-between items-center p-3 border border-gray-200 rounded-lg"
      >
        <div>
          <p className="font-medium text-left">{t('accountSettings.security.resetPassword')}</p>
          <p className="text-xs text-gray-500 text-left">{t('accountSettings.security.resetDescription')}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </button>
    );
  };

  // Render account deletion section
  const renderAccountDeletionSection = () => {
    if (deleteStatus.success) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800">{t('accountSettings.dangerZone.accountDeleted')}</p>
              <p className="text-xs text-green-600 mt-1">
                {t('accountSettings.dangerZone.deletedDescription')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">{t('accountSettings.dangerZone.title')}</p>
              <p className="text-xs text-red-600 mt-1">
                {t('accountSettings.dangerZone.warning')}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowDeleteModal(true)}
          className="w-full flex justify-center items-center gap-2 p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 className="h-5 w-5" />
          {t('accountSettings.dangerZone.deleteAccount')}
        </button>
      </div>
    );
  };

  // Delete confirmation modal
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-sm p-4">
          <div className="flex items-center mb-3">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <h3 className="text-base font-semibold">{t('accountSettings.dangerZone.confirmDelete')}</h3>
          </div>
          
          <p className="text-sm text-gray-600 mb-3">
            {t('accountSettings.dangerZone.confirmDescription')}
          </p>

          {userData?.subscription?.paymentPlatform === 'apple' &&
           userData?.userPlan === 'premium' &&
           userData?.subscription?.status !== 'canceled' &&
           userData?.subscription?.status !== 'expired' && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3">
              <p className="text-xs text-amber-700 mb-2">
                {t('accountSettings.dangerZone.appleWarning')}
              </p>
              <button
                onClick={handleManageSubscription}
                className="w-full py-2 bg-amber-500 text-white rounded-lg text-xs font-medium"
              >
                {t('accountSettings.dangerZone.cancelInAppStore')}
              </button>
            </div>
          )}
          
          <p className="text-xs text-gray-600 mb-2">
            {t('accountSettings.dangerZone.typeToConfirm')}
          </p>
          
          <input
  type="text"
  value={deleteConfirmation}
  onChange={(e) => setDeleteConfirmation(e.target.value)}
  placeholder={t('accountSettings.dangerZone.confirmationText')}
  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-3 text-base"
  disabled={deleteStatus.loading}
/>
          
          {deleteStatus.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <p className="text-xs text-red-600">{deleteStatus.error}</p>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmation('');
                setDeleteStatus({ loading: false, success: false, error: null });
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              disabled={deleteStatus.loading}
            >
              {t('accountSettings.dangerZone.cancel')}
            </button>
            
            <button
              onClick={handleDeleteAccount}
              disabled={deleteStatus.loading || deleteConfirmation.toLowerCase() !== 'goodbye easysubs'}
              className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {deleteStatus.loading ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('accountSettings.dangerZone.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-3 w-3" />
                  {t('accountSettings.dangerZone.deleteAccount')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Cancel modal for Stripe
  const renderCancelModal = () => {
    if (!showCancelModal) return null;

    const expiryDate = userData?.subscription?.expiryDate
      ? new Date(userData.subscription.expiryDate).toLocaleDateString()
      : t('accountSettings.subscription.yourBillingDate');

    return (
      <div className="fixed inset-0 bg-black/50 flex items-end z-50">
        <div className="bg-white w-full rounded-t-2xl p-6">
          <h3 className="font-bold text-gray-800 text-lg mb-2">
            {t('accountSettings.subscription.cancelModal.title')}
          </h3>
          <p className="text-gray-500 text-sm mb-2">
            {t('accountSettings.subscription.cancelModal.keepAccessUntil')} {expiryDate}.
          </p>
          <p className="text-gray-400 text-xs mb-5">
            {t('accountSettings.subscription.cancelModal.afterCancellation')}
          </p>

          {cancelStatus.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-600 text-sm">{cancelStatus.error}</p>
            </div>
          )}

          <button
            onClick={handleStripeCancel}
            disabled={cancellingSubscription}
            className="w-full py-3 bg-red-500 text-white rounded-lg font-medium mb-3 flex items-center justify-center"
          >
            {cancellingSubscription ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('common.cancelling')}
              </>
            ) : (
              t('accountSettings.subscription.cancelModal.confirm')
            )}
          </button>
          <button
            onClick={() => {
              setShowCancelModal(false);
              setCancelStatus({ success: false, error: null });
            }}
            disabled={cancellingSubscription}
            className="w-full py-3 text-gray-600 rounded-lg font-medium border border-gray-200"
          >
            {t('accountSettings.subscription.cancelModal.keep')}
          </button>
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 justify-center items-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />

      <main className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className={`${userData?.userPlan === 'premium' 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-4 rounded-lg transition-all duration-500`}
          >
            <h1 className="text-xl font-bold">{t('accountSettings.title')}</h1>
            <p className="text-white/90 text-sm mt-1">{t('accountSettings.subtitle')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#253985]" />
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 space-y-6">
              <div className="bg-yellow-50 border-yellow-200 rounded-lg p-3">
                <h3 className="text-yellow-800 font-medium">
                  {t('accountSettings.errorLoadingData')}
                </h3>
                <p className="text-yellow-600 text-xs mt-1">{error}</p>
              </div>
              
              {/* Personal Information */}
              <div className="space-y-4">
                <h2 className="font-semibold text-[#253985] flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  {t('accountSettings.personalInfo.title')}
                </h2>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">{t('accountSettings.personalInfo.firstName')}</p>
                    <p className="font-medium">
                      {user?.firstName || t('accountSettings.personalInfo.notProvided')}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">{t('accountSettings.personalInfo.email')}</p>
                    <p className="font-medium flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {user?.email || t('accountSettings.personalInfo.notProvided')}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">{t('accountSettings.personalInfo.country')}</p>
                    <p className="font-medium flex items-center">
                      <Globe className="h-4 w-4 mr-2 text-gray-400" />
                      {userData?.nation || t('accountSettings.personalInfo.notSpecified')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Security */}
              <div className="space-y-4">
                <h2 className="font-semibold text-[#253985] flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  {t('accountSettings.security.title')}
                </h2>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="font-medium text-left">{t('accountSettings.security.resetPassword')}</p>
                  <p className="text-xs text-gray-500 text-left mt-1">
                    {t('accountSettings.security.fixDataError')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h2 className="font-semibold text-[#253985] flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  {t('accountSettings.personalInfo.title')}
                </h2>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">{t('accountSettings.personalInfo.firstName')}</p>
                    <p className="font-medium">{userData?.firstName || userData?.companyName || t('accountSettings.personalInfo.notProvided')}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">{t('accountSettings.personalInfo.lastName')}</p>
                    <p className="font-medium">{userData?.lastName || t('accountSettings.personalInfo.notProvided')}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">{t('accountSettings.personalInfo.email')}</p>
                    <p className="font-medium flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {userData?.email}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500">{t('accountSettings.personalInfo.country')}</p>
                    <p className="font-medium flex items-center">
                      <Globe className="h-4 w-4 mr-2 text-gray-400" />
                      {userData?.nation || t('accountSettings.personalInfo.notSpecified')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Security */}
              <div className="space-y-4">
                <h2 className="font-semibold text-[#253985] flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  {t('accountSettings.security.title')}
                </h2>
                
                {renderSecuritySection()}
              </div>

              {/* Language Preference Section */}
              <div className="pt-4 border-t border-gray-200">
                <FlagLanguageSelector variant="mobile" />
              </div>

              {/* Subscription Plan */}
              <div className="pt-4 border-t border-gray-200">
                <h2 className="font-semibold text-[#253985] mb-3">{t('accountSettings.subscription.title')}</h2>
                
                <div className="space-y-3">
                  <div className={`p-3 rounded-lg border ${userData?.userPlan === 'premium' ? 'border-transparent bg-gradient-to-r from-[#253985] to-amber-500 text-white' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center mb-1">
                          <Crown className={`h-5 w-5 mr-2 ${userData?.userPlan === 'premium' ? 'text-amber-200' : 'text-amber-500'}`} />
                          <h3 className="font-medium">{t('accountSettings.subscription.premium.title')}</h3>
                        </div>
                        <p className={`text-xs ${userData?.userPlan === 'premium' ? 'text-white/90' : 'text-gray-500'}`}>
                          {t('accountSettings.subscription.premium.description')}
                        </p>
                      </div>
                      {userData?.userPlan === 'premium' ? (
                        <span className="bg-white text-[#253985] text-xs px-2 py-1 rounded-full font-semibold">
                          {t('accountSettings.subscription.premium.current')}
                        </span>
                      ) : null}
                    </div>
                    
                    {userData?.userPlan !== 'premium' && (
                      <button 
                        onClick={() => navigate('/mobile-upgrade')}
                        className="w-full py-2 bg-gradient-to-r from-[#253985] to-amber-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {t('accountSettings.subscription.upgrade')}
                      </button>
                    )}
                  </div>
                  
                  <div className={`p-3 rounded-lg border ${userData?.userPlan === 'free' ? 'border-[#253985] bg-[#253985]/10' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{t('accountSettings.subscription.free.title')}</h3>
                        <p className="text-xs text-gray-500 mt-1">{t('accountSettings.subscription.free.description')}</p>
                      </div>
                      {userData?.userPlan === 'free' && (
                        <span className="bg-[#253985] text-white text-xs px-2 py-1 rounded-full">
                          {t('accountSettings.subscription.free.current')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Manage Subscription UI for premium users */}
                  {userData?.userPlan === 'premium' && (
                    <div className="mt-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm font-medium text-gray-700">
                          {t('accountSettings.subscription.billing')}
                        </p>
                      </div>
                      {userData?.subscription?.expiryDate && (
                        <p className="text-xs text-gray-500 mb-3">
                          {cancelStatus.success
                            ? `${t('accountSettings.subscription.cancelsOn')} ${new Date(userData.subscription.expiryDate).toLocaleDateString()}`
                            : `${t('accountSettings.subscription.renewsOn')} ${new Date(userData.subscription.expiryDate).toLocaleDateString()}`
                          }
                        </p>
                      )}
                      {cancelStatus.success ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                          <p className="text-green-700 text-xs text-center">
                            {t('accountSettings.subscription.cancelledMessage')}
                          </p>
                        </div>
                      ) : (
                        <button
                          onClick={handleManageSubscription}
                          className="w-full py-2 border border-red-300 text-red-500 rounded-lg text-sm font-medium"
                        >
                          {userData?.subscription?.paymentPlatform === 'apple'
                            ? t('accountSettings.subscription.manageAppStore')
                            : t('accountSettings.subscription.cancelButton')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Account Deletion Section */}
              <div className="pt-4 border-t border-gray-200">
                <h2 className="font-semibold text-red-600 mb-3 flex items-center">
                  <Trash2 className="h-5 w-5 mr-2" />
                  {t('accountSettings.dangerZone.deleteAccount')}
                </h2>
                {renderAccountDeletionSection()}
              </div>
            </div>
          )}
        </div>
      </main>

      <MobileBottomNav 
        navItems={navItems} 
        currentPath={location.pathname}
        maxVisibleItems={5}
        navigate={navigate}
        className="fixed bottom-0 w-full"
      />

      {renderDeleteModal()}
      {renderCancelModal()}
    </div>
  );
};

export default MobileAccountSettings;